const EXIT_TYPES = new Set(['exit', 'partial_exit'])

function asNumber(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function isBuy(exec) {
  const side = String(exec?.side || '').toLowerCase()
  return side === 'buy' || side === 'buy_to_open'
}

function isSell(exec) {
  const side = String(exec?.side || '').toLowerCase()
  return side === 'sell' || side === 'sell_to_close'
}

function executionTime(exec) {
  const t = new Date(exec?.executed_at || 0).getTime()
  return Number.isFinite(t) ? t : 0
}

function roundCents(value) {
  return Math.round((Number(value) || 0) * 100) / 100
}

function roundQty(value) {
  return Math.round((Number(value) || 0) * 10000) / 10000
}

function removeEmptyLots(lots) {
  for (let i = lots.length - 1; i >= 0; i -= 1) {
    if (lots[i].qty <= 0.000001) lots.splice(i, 1)
  }
}

function relieveLots(lots, qtyToRelieve, method) {
  let remaining = qtyToRelieve
  let consumedBasis = 0

  const nextIndex = () => {
    if (method === 'FIFO') return 0
    return lots.length - 1
  }

  while (remaining > 0.000001 && lots.length > 0) {
    const idx = nextIndex()
    const lot = lots[idx]
    const usedQty = Math.min(lot.qty, remaining)
    const usedBasis = lot.qty > 0 ? lot.basis * (usedQty / lot.qty) : 0

    lot.qty = roundQty(lot.qty - usedQty)
    lot.basis = roundCents(lot.basis - usedBasis)
    consumedBasis += usedBasis
    remaining = roundQty(remaining - usedQty)
    removeEmptyLots(lots)
  }

  return { consumedBasis, unmatchedQty: remaining }
}

export function computeEquityLotAccounting(executions, {
  symbol,
  accountNumbers,
  markPrice,
  method = 'LIFO',
} = {}) {
  const normalizedMethod = String(method || '').toUpperCase()
  if (!['LIFO', 'FIFO'].includes(normalizedMethod)) {
    return { available: false, reason: 'unsupported_method' }
  }

  const wantedSymbol = symbol ? String(symbol).toUpperCase() : null
  const wantedAccounts = accountNumbers?.length
    ? new Set(accountNumbers.map(a => String(a)))
    : null

  const rows = (executions || [])
    .filter(exec => {
      if (wantedSymbol && String(exec?.symbol || '').toUpperCase() !== wantedSymbol) return false
      if (wantedAccounts && !wantedAccounts.has(String(exec?.account_number || ''))) return false
      return true
    })
    .filter(exec => asNumber(exec?.quantity) > 0 && asNumber(exec?.price) > 0)
    .sort((a, b) => executionTime(a) - executionTime(b))

  if (rows.length === 0) return { available: false, reason: 'no_executions' }

  const lotsByAccount = new Map()
  let realizedPnl = 0
  let unmatchedExitQty = 0

  for (const exec of rows) {
    const account = String(exec.account_number || '')
    if (!lotsByAccount.has(account)) lotsByAccount.set(account, [])
    const lots = lotsByAccount.get(account)

    const qty = asNumber(exec.quantity)
    const price = asNumber(exec.price)
    const commission = asNumber(exec.commission)
    const fees = asNumber(exec.fees)
    const gross = qty * price

    if (isBuy(exec) && exec.execution_type === 'entry') {
      lots.push({
        qty,
        basis: roundCents(gross + commission + fees),
        price,
        executedAt: exec.executed_at || null,
      })
      continue
    }

    if (isSell(exec) && EXIT_TYPES.has(exec.execution_type)) {
      const { consumedBasis, unmatchedQty } = relieveLots(lots, qty, normalizedMethod)
      unmatchedExitQty += unmatchedQty
      const matchedQty = qty - unmatchedQty
      const proceeds = matchedQty * price - commission - fees
      realizedPnl += proceeds - consumedBasis
    }
  }

  let remainingQty = 0
  let remainingBasis = 0
  for (const lots of lotsByAccount.values()) {
    for (const lot of lots) {
      remainingQty += lot.qty
      remainingBasis += lot.basis
    }
  }

  remainingQty = roundQty(remainingQty)
  remainingBasis = roundCents(remainingBasis)
  realizedPnl = roundCents(realizedPnl)

  const remainingAvgPrice = remainingQty > 0 ? remainingBasis / remainingQty : null
  const mark = markPrice == null ? null : asNumber(markPrice, NaN)
  const hasMark = mark != null && Number.isFinite(mark)
  const openPnl = hasMark ? roundCents((mark * remainingQty) - remainingBasis) : null
  const campaignPnl = openPnl == null ? realizedPnl : roundCents(realizedPnl + openPnl)

  return {
    available: remainingQty > 0 || rows.length > 0,
    method: normalizedMethod,
    remainingQty,
    remainingBasis,
    remainingAvgPrice,
    realizedPnl,
    openPnl,
    campaignPnl,
    unmatchedExitQty: roundQty(unmatchedExitQty),
    reason: unmatchedExitQty > 0 ? 'unmatched_exit_quantity' : null,
  }
}
